import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { Season } from '../types';

const TITLE_MAX = 120;

/**
 * Season heading with an unobtrusive inline rename. The calendar year is the
 * season's identity and stays visible as metadata once the title is custom.
 * Enter saves, Escape cancels.
 */
export default function SeasonTitle({
  season,
  onChange,
  owner,
  canEdit = true,
}: {
  season: Season;
  onChange: (updated: Season) => void;
  /** Set when the season belongs to someone who shared it. */
  owner?: string | null;
  canEdit?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(season.title);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const generated = season.generatedTitle ?? `Season ${season.year}`;

  // Focus follows the mode: into the field when editing starts, and back to
  // the button that started it when editing ends (save, cancel or Escape).
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing) inputRef.current?.select();
    else if (wasEditing.current) editButtonRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  const close = () => {
    setEditing(false);
    setError(null);
    setDraft(season.title);
  };

  const run = async (action: () => Promise<Season>) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await action();
      onChange(updated);
      setDraft(updated.title);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const trimmed = draft.replace(/\s+/g, ' ').trim();
  const invalid = !trimmed ? 'Give this season a name.' : trimmed.length > TITLE_MAX ? `Keep it to ${TITLE_MAX} characters.` : null;

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          if (trimmed === season.title) return close();
          void run(() => api.renameSeason(season.year, trimmed, owner));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            close();
          }
        }}
      >
        <label htmlFor="season-title" className="sr-only">
          Season name
        </label>
        <input
          id="season-title"
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-invalid={Boolean(invalid)}
          aria-describedby="season-title-help"
          className="focus-ring w-full max-w-2xl rounded-md border border-white/20 bg-black/50 px-3 py-2 text-2xl font-extrabold tracking-tight text-white sm:text-4xl"
        />
        <div id="season-title-help" className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <button
            type="submit"
            disabled={saving || Boolean(invalid)}
            className="focus-ring rounded-md bg-white px-4 py-1.5 font-bold text-black disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={close} className="focus-ring rounded-md bg-white/10 px-4 py-1.5 font-semibold">
            Cancel
          </button>
          {season.titleLocked && (
            <button
              type="button"
              onClick={() => void run(() => api.restoreSeason(season.year, owner))}
              className="focus-ring rounded text-white/50 underline decoration-white/20 underline-offset-4 hover:text-white"
            >
              Use “{generated}”
            </button>
          )}
          {(invalid || error) && <span className="text-red-300">{invalid ?? error}</span>}
        </div>
      </form>
    );
  }

  return (
    <div className="group">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">{season.title}</h1>
        {canEdit && (
        <button
          ref={editButtonRef}
          onClick={() => setEditing(true)}
          className="focus-ring flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-white/40 transition hover:bg-white/10 hover:text-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 20h4L19 9l-4-4L4 16v4z" strokeLinejoin="round" />
          </svg>
          Rename season
        </button>
        )}
      </div>
      {season.titleLocked && <p className="mt-1 text-sm font-medium text-white/45">{season.year}</p>}
    </div>
  );
}
